/**
 * AI Response Modes System
 * 
 * Different thinking depths and behaviors for different tasks.
 * Inspired by Copilot's mode system.
 * 
 * Free users: Quick response only
 * Premium users: All modes unlocked
 */

import type { SubscriptionPlan } from "@/lib/subscription/plans";
import { getPlanFeatures } from "@/lib/subscription/plans";

export type ResponseMode =
  | "auto"
  | "quick"
  | "think_deeper"
  | "smart"
  | "study"
  | "deep_research"
  | "search"
  | "structured";

export interface ResponseModeConfig {
  mode: ResponseMode;
  name: string;
  description: string;
  timeEstimate: string;
  icon: string;
  requiresPremium: boolean;
  thinkingDepth: "minimal" | "standard" | "extended" | "maximum";
  useAtlas: boolean;
  useWebSearch: boolean;
  maxTokens: number;
  temperature: number;
}

export const RESPONSE_MODES: Record<ResponseMode, ResponseModeConfig> = {
  auto: {
    mode: "auto",
    name: "Auto",
    description: "Automatically detects what you need",
    timeEstimate: "Varies",
    icon: "🎯",
    requiresPremium: false,
    thinkingDepth: "standard",
    useAtlas: true,
    useWebSearch: true,
    maxTokens: 4096,
    temperature: 0.7,
  },
  quick: {
    mode: "quick",
    name: "Quick response",
    description: "Best for everyday conversation",
    timeEstimate: "2-3 sec",
    icon: "⚡",
    requiresPremium: false,
    thinkingDepth: "minimal",
    useAtlas: false,
    useWebSearch: false,
    maxTokens: 2048,
    temperature: 0.7,
  },
  think_deeper: {
    mode: "think_deeper",
    name: "Think Deeper",
    description: "Better for more complex topics",
    timeEstimate: "~30 sec",
    icon: "⚙️",
    requiresPremium: true,
    thinkingDepth: "extended",
    useAtlas: false,
    useWebSearch: false,
    maxTokens: 4096,
    temperature: 0.5,
  },
  smart: {
    mode: "smart",
    name: "Smart",
    description: "Thinks quickly or deeply based on the task",
    timeEstimate: "≈ 5–10 seconds",
    icon: "✨",
    requiresPremium: true,
    thinkingDepth: "standard",
    useAtlas: true,
    useWebSearch: false,
    maxTokens: 4096,
    temperature: 0.6,
  },
  study: {
    mode: "study",
    name: "Study and learn",
    description: "Guided learning instead of direct answers",
    timeEstimate: "~1 min",
    icon: "🎓",
    requiresPremium: true,
    thinkingDepth: "extended",
    useAtlas: true,
    useWebSearch: false,
    maxTokens: 4096,
    temperature: 0.5,
  },
  deep_research: {
    mode: "deep_research",
    name: "Deep Research",
    description: "Detailed report with references",
    timeEstimate: "10-15 min",
    icon: "🎯",
    requiresPremium: true,
    thinkingDepth: "maximum",
    useAtlas: true,
    useWebSearch: true,
    maxTokens: 8192,
    temperature: 0.4,
  },
  search: {
    mode: "search",
    name: "Search",
    description: "Answers with enhanced references",
    timeEstimate: "~10-20 sec",
    icon: "🔍",
    requiresPremium: true,
    thinkingDepth: "standard",
    useAtlas: true,
    useWebSearch: true,
    maxTokens: 4096,
    temperature: 0.6,
  },
  structured: {
    mode: "structured",
    name: "Structured",
    description: "Plan then answer — shows reasoning steps",
    timeEstimate: "~15-30 sec",
    icon: "📋",
    requiresPremium: true,
    thinkingDepth: "extended",
    useAtlas: true,
    useWebSearch: false,
    maxTokens: 4096,
    temperature: 0.5,
  },
};

const ENABLE_SUBSCRIPTION_LIMITS =
  typeof process !== "undefined" && process.env?.ENABLE_SUBSCRIPTION_LIMITS === "true";

/**
 * Get available response modes for a subscription plan.
 * Set ENABLE_SUBSCRIPTION_LIMITS=true to restrict free plan to Quick only.
 */
export function getAvailableModes(plan: SubscriptionPlan): ResponseMode[] {
  if (!ENABLE_SUBSCRIPTION_LIMITS) {
    return Object.keys(RESPONSE_MODES) as ResponseMode[];
  }
  const planFeatures = getPlanFeatures(plan);
  if (plan === "free") {
    return ["quick"];
  }
  return Object.keys(RESPONSE_MODES) as ResponseMode[];
}

/**
 * Check if a mode is available for a plan
 */
export function isModeAvailable(mode: ResponseMode, plan: SubscriptionPlan): boolean {
  const availableModes = getAvailableModes(plan);
  return availableModes.includes(mode);
}

/**
 * Auto-detect best mode for a query (for Smart mode and Auto mode)
 */
export function detectBestMode(query: string): ResponseMode {
  const lowerQuery = query.toLowerCase();
  
  // Image generation → Auto handles it
  if (
    lowerQuery.match(/(generate|create|make|draw|show me).*(image|photo|picture|visual|art|illustration)/i) ||
    lowerQuery.match(/(image|photo|picture) of/i) ||
    lowerQuery.includes("generate me a photo") ||
    lowerQuery.includes("create an image") ||
    lowerQuery.includes("generate a photo")
  ) {
    return "auto"; // Auto mode handles image generation
  }
  
  // Video generation → Auto handles it
  if (
    lowerQuery.match(/(generate|create|make).*(video|movie|clip|animation)/i) ||
    lowerQuery.match(/(video|movie) of/i) ||
    lowerQuery.includes("generate me a video")
  ) {
    return "auto"; // Auto mode handles video generation
  }
  
  // Simple questions → Quick
  if (
    lowerQuery.length < 50 ||
    lowerQuery.match(/^(what|who|when|where|how|why)\s+is/i) ||
    lowerQuery.match(/\?$/) && lowerQuery.split(" ").length < 10
  ) {
    return "quick";
  }
  
  // Research questions → Deep Research
  if (
    lowerQuery.includes("research") ||
    lowerQuery.includes("analyze") ||
    lowerQuery.includes("comprehensive") ||
    lowerQuery.includes("detailed report")
  ) {
    return "deep_research";
  }
  
  // Learning questions → Study
  if (
    lowerQuery.includes("learn") ||
    lowerQuery.includes("teach") ||
    lowerQuery.includes("explain step by step") ||
    lowerQuery.includes("how do i")
  ) {
    return "study";
  }
  
  // Information lookup → Search
  if (
    lowerQuery.includes("latest") ||
    lowerQuery.includes("current") ||
    lowerQuery.includes("find") ||
    lowerQuery.includes("search for")
  ) {
    return "search";
  }
  
  // Complex topics → Think Deeper
  if (
    lowerQuery.split(" ").length > 20 ||
    lowerQuery.includes("complex") ||
    lowerQuery.includes("complicated")
  ) {
    return "think_deeper";
  }
  
  // Default → Smart (adaptive)
  return "smart";
}

/**
 * Get mode configuration
 */
export function getModeConfig(mode: ResponseMode): ResponseModeConfig {
  return RESPONSE_MODES[mode];
}

